<template>
  <!--
    App shell sidebar (design 1b wide / collapsed icons, 1g tablet rail).

    Presentational: it never mutates props, never calls the store or the router, never reads $route.
    The shell (AppShell.vue) owns the arrangement and its save pipeline; this component only EMITS:
      select(item) · reorder({ pinned, more }) · pin(id) · unpin(id) · toggle-collapsed ·
      toggle-more · open-flyout · close-flyout · reset-layout
    Drag: vuedraggable lists in ONE group ('app-shell-nav') — Pinned, More (wide) and the More flyout
    (icons / rail) — over LOCAL copies of the id arrays, re-synced from props by a watcher. A drop
    emits ONE `reorder` with the full new arrays (nothing when the order did not change).
  -->
  <aside
    class="app-sidebar"
    :class="[`app-sidebar--${variant}`, { 'app-sidebar--editable': canEdit, 'app-sidebar--dragging': dragging }]"
    data-test="app-shell-sidebar-panel"
    aria-label="Main navigation"
  >
    <!-- ================================ wide (1b) ================================ -->
    <template v-if="variant === 'wide'">
      <div class="app-sidebar__brand">
        <!-- The Teamio mark (Claude Design "logo-workflow-icon", Filled): its own two colours, not $fu-brand. -->
        <svg class="app-sidebar__logo" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
          <rect width="8" height="8" x="3" y="3" rx="2" fill="#0c0f0e" stroke="#0c0f0e" />
          <path d="M7 11v4a2 2 0 0 0 2 2h4" stroke="#0c0f0e" />
          <rect width="8" height="8" x="13" y="13" rx="2" fill="#16a583" stroke="#16a583" />
        </svg>
        <span class="app-sidebar__wordmark">teamio</span>
        <button
          type="button"
          class="app-sidebar__icon-btn app-sidebar__collapse"
          data-test="app-nav-collapse"
          title="Collapse to icons"
          aria-label="Collapse to icons"
          @click="$emit('toggle-collapsed')"
        >
          <AppIcon name="panelClose" :size="18" />
        </button>
      </div>

      <nav class="app-sidebar__scroll" aria-label="Apps">
        <div class="app-sidebar__label">Pinned</div>
        <draggable
          class="app-sidebar__list"
          tag="div"
          data-test="app-nav-pinned"
          v-bind="dragOptions"
          :value="localPinned"
          @start="onDragStart"
          @input="onPinnedInput"
          @end="onDragEnd"
        >
          <AppNavItem
            v-for="row in pinnedRows"
            :key="row.id"
            :item="row"
            :active="row.id === activeId"
            :pinned="true"
            variant="wide"
            :editable="canEdit"
            @select="onSelect"
            @pin-toggle="onPinToggle(true, $event)"
          />
          <template #footer>
            <div v-if="!pinnedRows.length" class="app-sidebar__empty" data-test="app-nav-pinned-empty">
              {{ canEdit ? 'Drag apps here to pin them' : 'No pinned apps' }}
            </div>
          </template>
        </draggable>

        <button
          type="button"
          class="app-sidebar__label app-sidebar__more-toggle"
          data-test="app-nav-more-toggle"
          :aria-expanded="moreOpen ? 'true' : 'false'"
          @click="$emit('toggle-more')"
        >
          More apps<span class="app-sidebar__more-n">{{ moreRows.length }}</span>
          <span class="app-sidebar__more-chev"><AppIcon :name="moreOpen ? 'chev' : 'chevR'" :size="14" /></span>
        </button>

        <draggable
          v-if="moreOpen"
          class="app-sidebar__list"
          tag="div"
          data-test="app-nav-more"
          v-bind="dragOptions"
          :value="localMore"
          @start="onDragStart"
          @input="onMoreInput"
          @end="onDragEnd"
        >
          <AppNavItem
            v-for="row in moreRows"
            :key="row.id"
            :item="row"
            :active="row.id === activeId"
            :pinned="false"
            variant="wide"
            :editable="canEdit"
            @select="onSelect"
            @pin-toggle="onPinToggle(false, $event)"
          />
        </draggable>

        <button
          v-if="showReset"
          type="button"
          class="app-sidebar__reset"
          data-test="app-nav-reset"
          @click="$emit('reset-layout')"
        >Reset to default</button>
      </nav>

      <div class="app-sidebar__foot">
        <a
          class="app-sidebar__help"
          data-test="app-nav-help"
          :href="help.href"
          target="_blank"
          rel="noopener"
        >
          <span class="app-sidebar__help-icon"><AppIcon :name="help.icon || 'help'" :size="18" /></span>
          {{ help.label }}
          <span class="app-sidebar__help-ext"><AppIcon name="ext" :size="14" /></span>
        </a>
      </div>
    </template>

    <!-- ===================== icons (collapsed 1b) / rail (tablet 1g) ===================== -->
    <template v-else>
      <div class="app-sidebar__rail-logo">
        <svg class="app-sidebar__logo" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
          <rect width="8" height="8" x="3" y="3" rx="2" fill="#0c0f0e" stroke="#0c0f0e" />
          <path d="M7 11v4a2 2 0 0 0 2 2h4" stroke="#0c0f0e" />
          <rect width="8" height="8" x="13" y="13" rx="2" fill="#16a583" stroke="#16a583" />
        </svg>
      </div>

      <nav class="app-sidebar__rail-scroll" aria-label="Apps">
        <draggable
          class="app-sidebar__rail-list"
          tag="div"
          data-test="app-nav-pinned"
          v-bind="dragOptions"
          :value="localPinned"
          @start="onDragStart"
          @input="onPinnedInput"
          @end="onDragEnd"
        >
          <AppNavItem
            v-for="row in pinnedRows"
            :key="row.id"
            :item="row"
            :active="row.id === activeId"
            :pinned="true"
            :variant="variant"
            :editable="canEdit"
            @select="onSelect"
            @pin-toggle="onPinToggle(true, $event)"
          />
        </draggable>

        <button
          type="button"
          class="app-sidebar__tile-btn"
          :class="{ 'is-open': flyoutOpen }"
          data-test="app-nav-flyout-toggle"
          :aria-expanded="flyoutOpen ? 'true' : 'false'"
          aria-label="More apps"
          :title="variant === 'icons' ? 'More apps' : null"
          @click="$emit(flyoutOpen ? 'close-flyout' : 'open-flyout')"
        >
          <span class="app-sidebar__tile-icon"><AppIcon name="grid" :size="variant === 'rail' ? 21 : 19" /></span>
          <span v-if="variant === 'rail'" class="app-sidebar__tile-short">More</span>
        </button>
      </nav>

      <div class="app-sidebar__rail-foot">
        <a
          class="app-sidebar__tile-btn"
          data-test="app-nav-help"
          :href="help.href"
          target="_blank"
          rel="noopener"
          :aria-label="help.label"
          :title="variant === 'icons' ? help.label : null"
        >
          <span class="app-sidebar__tile-icon"><AppIcon :name="help.icon || 'help'" :size="variant === 'rail' ? 21 : 19" /></span>
          <span v-if="variant === 'rail'" class="app-sidebar__tile-short">Help</span>
        </a>
        <button
          v-if="variant === 'icons'"
          type="button"
          class="app-sidebar__tile-btn"
          data-test="app-nav-expand"
          title="Show labels"
          aria-label="Show labels"
          @click="$emit('toggle-collapsed')"
        >
          <span class="app-sidebar__tile-icon"><AppIcon name="panelOpen" :size="19" /></span>
        </button>
      </div>

      <AppMoreFlyout
        :open="flyoutOpen"
        :items="moreRows"
        :can-edit="canEdit"
        :active-id="activeId"
        :touch="touch"
        :show-reset="showReset"
        @select="onSelect"
        @pin="$emit('pin', $event)"
        @close="$emit('close-flyout')"
        @reset="$emit('reset-layout')"
        @drag-start="onDragStart"
        @update-list="onMoreInput"
        @drag-end="onDragEnd"
      />
    </template>
  </aside>
</template>

<script>
import draggable from 'vuedraggable';
import AppIcon from './AppIcon.vue';
import AppNavItem from './AppNavItem.vue';
import AppMoreFlyout from './AppMoreFlyout.vue';

/** The one Sortable group every shell list joins, so rows move between Pinned and More. */
export const NAV_DRAG_GROUP = 'app-shell-nav';

const DEFAULT_HELP = { label: 'Teamio how-to', href: 'https://teamio.app/help', icon: 'help' };

function sameIds(a, b) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) if (a[i] !== b[i]) return false;
  return true;
}

export default {
  name: 'AppSidebar',
  components: { draggable, AppIcon, AppNavItem, AppMoreFlyout },
  props: {
    /** Resolved app rows: { id, label, shortLabel, icon, to, count?: number|null, dot?: boolean }. */
    items: { type: Array, default: () => [] },
    /** Pinned ids, in order. */
    pinned: { type: Array, default: () => [] },
    /** More ids, in order. */
    more: { type: Array, default: () => [] },
    activeId: { type: String, default: null },
    variant: { type: String, default: 'wide', validator: v => ['wide', 'icons', 'rail'].includes(v) },
    /** Admin / Super Admin may rearrange (drag, pin, reset). */
    canEdit: { type: Boolean, default: false },
    /** Wide: the More apps section is expanded. */
    moreOpen: { type: Boolean, default: true },
    /** Icons / rail: the More apps flyout is open. */
    flyoutOpen: { type: Boolean, default: false },
    /** The fixed bottom help row (registry HELP_LINK). */
    helpLink: { type: Object, default: null },
    /** Tablet: press-and-hold (250ms) before a drag starts. */
    touch: { type: Boolean, default: false },
    /** The arrangement equals the default — hides "Reset to default". */
    isDefault: { type: Boolean, default: true },
  },
  data() {
    return {
      localPinned: [],
      localMore: [],
      dragging: false,
    };
  },
  computed: {
    byId() {
      const map = {};
      this.items.forEach(row => {
        if (row && row.id) map[row.id] = row;
      });
      return map;
    },
    /** Props filtered to ids that have a row (a list index must match a rendered child). */
    propPinned() {
      return this.knownIds(this.pinned);
    },
    propMore() {
      return this.knownIds(this.more);
    },
    pinnedRows() {
      return this.localPinned.map(id => this.byId[id]).filter(Boolean);
    },
    moreRows() {
      return this.localMore.map(id => this.byId[id]).filter(Boolean);
    },
    help() {
      return this.helpLink || DEFAULT_HELP;
    },
    showReset() {
      return this.canEdit && !this.isDefault;
    },
    dragOptions() {
      // vuedraggable forwards every non-prop attr (camelized) to Sortable — delayOnTouchOnly must be
      // bound (a bare attribute would arrive as '' = false).
      return {
        group: NAV_DRAG_GROUP,
        draggable: '.app-nav-item',
        filter: '.app-nav-item__pin',
        preventOnFilter: false,
        disabled: !this.canEdit,
        delay: this.touch ? 250 : 0,
        delayOnTouchOnly: true,
        touchStartThreshold: 5,
        animation: 150,
        ghostClass: 'app-nav-ghost',
        chosenClass: 'app-nav-chosen',
      };
    },
  },
  watch: {
    propPinned: { handler: 'syncFromProps', immediate: true },
    propMore: 'syncFromProps',
  },
  methods: {
    knownIds(list) {
      const seen = {};
      return (Array.isArray(list) ? list : []).filter(id => {
        if (!this.byId[id] || seen[id]) return false;
        seen[id] = true;
        return true;
      });
    },
    syncFromProps() {
      if (this.dragging) return;
      this.localPinned = this.propPinned.slice();
      this.localMore = this.propMore.slice();
    },
    onSelect(item) {
      this.$emit('select', item);
    },
    onPinToggle(isPinned, id) {
      this.$emit(isPinned ? 'unpin' : 'pin', id);
    },
    onDragStart() {
      this.dragging = true;
    },
    onPinnedInput(ids) {
      this.localPinned = ids.slice();
    },
    onMoreInput(ids) {
      this.localMore = ids.slice();
    },
    /**
     * Sortable fires add / remove (→ @input, synchronous) before end, so both local lists already
     * hold the drop. Emit ONE reorder when they differ from the props, then fall back to the props
     * (the shell applies the change optimistically; if it refuses, the old order comes back).
     */
    onDragEnd() {
      this.dragging = false;
      const pinned = this.localPinned.slice();
      const more = this.localMore.slice();
      if (!sameIds(pinned, this.propPinned) || !sameIds(more, this.propMore)) {
        this.$emit('reorder', { pinned, more });
      }
      this.$nextTick(this.syncFromProps);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './shell';

.app-sidebar {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  z-index: $shell-z-chrome;
  display: flex;
  flex-direction: column;
  background: $fu-surface;
  border-right: 1px solid $fu-border;
  color: $fu-text;
  text-align: left;
  @include fu-font-base;
  @media print { display: none; }
}

.app-sidebar--wide { width: $shell-sidebar-wide; }
.app-sidebar--icons,
.app-sidebar--rail {
  width: $shell-sidebar-rail;
  align-items: center;
  padding: 14px 0 12px;
}

// ---- brand row ---------------------------------------------------------------------------------
.app-sidebar__brand {
  height: $shell-bar-height;
  flex: none;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 10px 0 16px;
}
.app-sidebar__logo {
  display: block;
  flex: none;
}
.app-sidebar__wordmark {
  @include fu-font-display;
  font-weight: 800;
  font-size: 19px;
  letter-spacing: -0.02em;
  color: $fu-text-strong;
}
.app-sidebar__icon-btn {
  @include fu-icon-btn(32px);
  margin-left: auto;
}

// ---- wide lists --------------------------------------------------------------------------------
.app-sidebar__scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 4px 10px 12px;
}
.app-sidebar__label {
  @include shell-section-label;
}
.app-sidebar__list {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-height: 36px;
}
.app-sidebar__empty {
  height: 40px;
  border-radius: 9px;
  border: 1.5px dashed $fu-border-strong;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: $fu-neutral-500;
}
.app-sidebar__more-toggle {
  @include fu-button-reset;
  // The reset's `font: inherit; color: inherit` would undo .app-sidebar__label (11px / 700 / grey),
  // so "MORE APPS" drew bigger, lighter and darker than "PINNED". Re-apply the label after it.
  @include shell-section-label;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 14px;
  cursor: pointer;
  user-select: none;
  &:hover { color: $fu-text; }
  @include fu-focus-ring;
}
.app-sidebar__more-n {
  letter-spacing: 0;
  color: $fu-neutral-400;
}
.app-sidebar__more-chev {
  margin-left: auto;
  display: flex;
}
.app-sidebar__reset {
  @include fu-link-btn;
  margin: 10px 10px 0;
  font-size: 12.5px;
}

.app-sidebar__foot {
  flex: none;
  padding: 8px 10px 10px;
  border-top: 1px solid $fu-border;
}
.app-sidebar__help {
  display: flex;
  align-items: center;
  gap: 10px;
  height: $shell-row-height;
  padding: 0 10px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: $fu-fw-medium;
  color: $fu-neutral-600;
  text-decoration: none;
  &:hover,
  &:focus {
    background: $fu-neutral-50;
    color: $fu-neutral-600;
    text-decoration: none;
  }
  @include fu-focus-ring;
}
.app-sidebar__help-icon {
  display: flex;
  color: $fu-neutral-500;
}
.app-sidebar__help-ext {
  margin-left: auto;
  display: flex;
  color: $fu-neutral-400;
}

// ---- icons / rail ------------------------------------------------------------------------------
.app-sidebar__rail-logo {
  flex: none;
  margin-bottom: 14px;
}
.app-sidebar__rail-scroll {
  flex: 1;
  min-height: 0;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 3px; // room for the count badge on the first tile
  &::-webkit-scrollbar { display: none; }
}
.app-sidebar__rail-list {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-height: 44px;
}
.app-sidebar--rail .app-sidebar__rail-list { gap: 2px; }
.app-sidebar__rail-foot {
  flex: none;
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}
.app-sidebar__tile-btn {
  @include fu-button-reset;
  flex: none;
  margin-top: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: $fu-neutral-500;
  text-decoration: none;
  &:hover,
  &:focus {
    color: $fu-neutral-600;
    text-decoration: none;
  }
  @include fu-focus-ring;
}
.app-sidebar__tile-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}
.app-sidebar--icons {
  .app-sidebar__tile-btn {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    &:hover,
    &.is-open { background: $fu-neutral-100; }
  }
}
.app-sidebar--rail {
  .app-sidebar__tile-btn {
    width: 60px;
    min-height: 52px;
    gap: 3px;
    border-radius: 12px;
    color: $fu-neutral-600;
    -webkit-tap-highlight-color: transparent;
  }
  .app-sidebar__tile-icon {
    width: 44px;
    height: 28px;
    border-radius: 14px;
  }
  .app-sidebar__tile-btn.is-open .app-sidebar__tile-icon { background: $fu-neutral-100; }
  .app-sidebar__tile-short {
    font-size: 11px;
    font-weight: $fu-fw-semibold;
    line-height: 1.15;
  }
}

// ---- drag feedback (design: dashed teal slot where the row will land) ---------------------------
// The ghost is an AppNavItem root (it carries this component's scope id); its children do not.
.app-sidebar ::v-deep .app-nav-ghost {
  background: $fu-brand-50 !important;
  border: 1.5px dashed $fu-brand-300;
  border-radius: 8px;
  > * { visibility: hidden; }
}
.app-sidebar--icons .app-sidebar__rail-list ::v-deep .app-nav-ghost {
  width: 40px;
  height: 40px;
  border-radius: 10px;
}
.app-sidebar--rail .app-sidebar__rail-list ::v-deep .app-nav-ghost {
  width: 60px;
  height: 52px;
  border-radius: 12px;
}
// A rail tile dropped into the flyout takes the flyout row shape.
.app-sidebar ::v-deep .app-more-flyout__list .app-nav-ghost {
  width: 100%;
  height: $shell-row-height;
}
.app-sidebar--dragging ::v-deep .app-nav-item__tip { display: none !important; }
</style>
